import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { useFollowUpAppointment } from "../../../entities/appointment";
import { FieldWrapper, FormInput, FormSelect, Input } from "../../../shared/ui";
import { DurationSpinner } from "../../../shared/ui/controls/DurationSpinner/DurationSpinner";
import Modal from "../../../shared/ui/overlay/Modal/Modal";
import { round15 } from "../../../shared/lib/round15";

function getNowTimeString(): string {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

function elapsedMinutesSince(iso: string): number {
  const start = new Date(iso).getTime()
  const diff = (Date.now() - start) / 60_000
  return diff > 0 ? diff : 0
}

const REASON_OPTIONS = [
  { value: "Mieter war nicht vor Ort", label: "Mieter war nicht vor Ort" },
  { value: "Termin nicht eingehalten", label: "Termin nicht eingehalten" },
  { value: "Weiterer Grund", label: "Weiterer Grund" },
  { value: "Mit anderem Gewerk", label: "Mit anderem Gewerk" },
  { value: "Material Bestellung", label: "Material Bestellung" },
];

const MATERIAL_BESTELLUNG = "Material Bestellung";

const FormSchema = z.object({
  followUpReason: z.string().min(1, "Bitte Begründung auswählen"),
  materialDetails: z.string().optional(),
  actualWorkDuration: z.number().int().min(0).multipleOf(15),
  endTime: z.string().regex(/^\d{2}:\d{2}$/),
  technicianComment: z.string().optional(),
});

type FormValues = z.infer<typeof FormSchema>;

type Props = {
  show: boolean;
  setShow: (show: boolean) => void;
  appointmentId: string;
  plannedWorkDuration: number;
  plannedDepartureDuration: number;
  actualStart: string | null;
  onSuccess: () => void;
};

export const FollowUpAppointmentModal = ({
  show,
  setShow,
  appointmentId,
  plannedWorkDuration,
  plannedDepartureDuration,
  actualStart,
  onSuccess,
}: Props) => {
  const followUp = useFollowUpAppointment();
  const [showComment, setShowComment] = useState(false);

  const initialWorkDuration = actualStart
    ? round15(elapsedMinutesSince(actualStart))
    : round15(plannedWorkDuration);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      followUpReason: "",
      materialDetails: "",
      actualWorkDuration: initialWorkDuration,
      endTime: getNowTimeString(),
      technicianComment: "",
    },
  });

  const selectedReason = useWatch({ control, name: "followUpReason" });
  const isMaterial = selectedReason === MATERIAL_BESTELLUNG;

  useEffect(() => {
    if (show) {
      setShowComment(false);
      const workDefault = actualStart
        ? round15(elapsedMinutesSince(actualStart))
        : round15(plannedWorkDuration);
      reset({
        followUpReason: "",
        materialDetails: "",
        actualWorkDuration: workDefault,
        endTime: getNowTimeString(),
        technicianComment: "",
      });
    }
  }, [show, plannedWorkDuration, actualStart, reset]);

  const onSubmit = (values: FormValues) => {
    const followUpReason = isMaterial && values.materialDetails
      ? `${MATERIAL_BESTELLUNG}: ${values.materialDetails}`
      : values.followUpReason;

    followUp.mutate(
      {
        id: appointmentId,
        dto: {
          followUpReason,
          actualWorkDuration: values.actualWorkDuration,
          actualDepartureDuration: plannedDepartureDuration === 0 ? 0 : null,
          endTime: values.endTime,
          technicianComment: values.technicianComment?.trim() || undefined,
        },
      },
      { onSuccess },
    );
  };

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Folgetermin anlegen"
      submitButton={{ onSubmit: handleSubmit(onSubmit), title: 'Speichern', isPending: followUp.isPending }}
    >
      <div className="flex flex-col gap-4 w-full">
        <FieldWrapper label="Begründung">
          <FormSelect
            name="followUpReason"
            control={control}
            options={REASON_OPTIONS}
            placeholder="Begründung auswählen"
          />
        </FieldWrapper>

        {isMaterial && (
          <FieldWrapper label="Details">
            <Controller
              name="materialDetails"
              control={control}
              render={({ field }) => (
                <Input
                  value={field.value ?? ""}
                  onChange={(val) => field.onChange(val)}
                  placeholder="Material beschreiben..."
                  error={errors.materialDetails?.message ?? null}
                />
              )}
            />
          </FieldWrapper>
        )}

        <Controller
          name="actualWorkDuration"
          control={control}
          render={({ field }) => (
            <DurationSpinner
              label="Nettoarbeitszeit (min)"
              value={field.value}
              onChange={field.onChange}
              error={errors.actualWorkDuration?.message}
            />
          )}
        />

        <FieldWrapper label="Endzeit">
          <FormInput name="endTime" control={control} type="time" />
        </FieldWrapper>

        <div className="mt-1">
          <button
            type="button"
            onClick={() => setShowComment((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              className={`w-4 h-4 transition-transform ${showComment ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            Kommentar hinterlassen
          </button>

          {showComment && (
            <div className="mt-2">
              <Controller
                name="technicianComment"
                control={control}
                render={({ field }) => (
                  <Input
                    as="textarea"
                    rows={3}
                    value={field.value ?? ""}
                    onChange={(val) => field.onChange(val)}
                    placeholder="Kommentar eingeben..."
                    error={errors.technicianComment?.message ?? null}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
